import { intersect, subtract, unify } from '@flatten-js/boolean-op';
import { Line, Matrix, ORIENTATION, Point, Polygon, Segment, Vector } from '@flatten-js/core';
import { multiSplitPolygons } from './poly-split';
import { createPolygonsFromFaces, trapezoidFromCenter, trapezoidFromSide } from './poly-split-breakerboard';

const RAD90 = Math.PI / 2;

/**
 *
 *
 * @param {Flatten.Vector} vectorA
 * @param {Flatten.Vector} vectorB
 * @returns {number} Smallest angle between A and B, always positive and <= PI/2
 */
function smallestAngleBetween(vectorA, vectorB) {
  const difference = vectorA.angleTo(vectorB);
  let smallAngle;
  if (difference > (3 * Math.PI) / 2) {
    smallAngle = Math.abs(difference - 2 * Math.PI);
  } else if (difference > Math.PI / 2) {
    smallAngle = Math.abs(difference - Math.PI);
  } else {
    smallAngle = difference;
  }
  return smallAngle;
}

function removeDuplicatedLines(lines) {
  const uniqueLines = [...lines];
  for (let i = 0; i < uniqueLines.length; i++) {
    const line = uniqueLines[i];
    if (i + 1 < uniqueLines.length) {
      const indicesToRemove = [];
      uniqueLines.slice(i + 1).forEach((otherLine, otherIndex) => {
        if (line.incidentTo(otherLine)) {
          indicesToRemove.push(otherIndex + 1);
        }
      });
      indicesToRemove.forEach((index, removedIndexes) => uniqueLines.splice(index - removedIndexes, 1));
    }
  }
  return uniqueLines;
}

/**
 * An inner border is a border which produces a dent on a polygon
 *
 * @param {Flatten.Vector} border - Border to check
 * @param {Flatten.Vector} prevBorder - Previous border
 * @param {Flatten.Vector} nextBorder - Next border
 * @param {boolean} isPolygonCW - Polygon orientation
 * @returns {boolean}
 */
function isInnerBorder(border, prevBorder, nextBorder, isPolygonCW) {
  return (
    (!isPolygonCW && (prevBorder.cross(border) < 0 || border.cross(nextBorder) < 0)) ||
    (isPolygonCW && (prevBorder.cross(border) > 0 || border.cross(nextBorder) > 0))
  );
}

/**
 * An inner border is a border which produces a dent on a polygon
 *
 * @param {Flatten.Edge} edge - Polygon edge to check
 * @param {boolean} isPolygonCW - Polygon orientation
 * @returns {boolean}
 */
function isInnerEdge(edge, isPolygonCW) {
  const border = new Vector(edge.shape.ps, edge.shape.pe);
  const prevBorder = new Vector(edge.prev.shape.ps, edge.prev.shape.pe);
  const nextBorder = new Vector(edge.next.shape.ps, edge.next.shape.pe);
  return isInnerBorder(border, prevBorder, nextBorder, isPolygonCW);
}

/**
 * An outer border is a polygon border which doesn't produce a dent
 *
 * @param {Flatten.Vector} border - Border to check
 * @param {Flatten.Vector} prevBorder - Previous border
 * @param {Flatten.Vector} nextBorder - Next border
 * @param {boolean} isPolygonCW - Polygon orientation
 * @returns {boolean}
 */
function isOuterBorder(border, prevBorder, nextBorder, isPolygonCW) {
  return (
    (!isPolygonCW && (prevBorder.cross(border) > 0 && border.cross(nextBorder) > 0)) ||
    (isPolygonCW && (prevBorder.cross(border) < 0 && border.cross(nextBorder) < 0))
  );
}

/**
 * This function is needed because in version 1.0.17 polygon.contains(segment) has erratic behaviour
 * 5 points (testPoints) are selected along the segment and each is tested to see if it falls inside the polygon.
 *
 * @param {Flatten.Polygon} polygon - Polygon to check if segment is inside
 * @param {Flatten.Segment} segment - The segment to test
 * @param {number} [testPoints=5] - Number of points to check if inside the polygon. Higher points, higher exec. time, higher precision
 * @param {number} [tolerance=0.0001]
 * @returns {boolean} - True if the segment is inside the polygon (all testPoints are inside), false otherwise
 */
function checkIfContains(polygon, segment, testPoints = 5, tolerance = 0.0001) {
  const edgePointsBorderDistance = 3 * tolerance;
  const deltaLength = (segment.length - 2 * edgePointsBorderDistance) / (testPoints - 1);
  const deltaVectorNorm = new Vector(segment.ps, segment.pe).normalize();
  const vectorToStart = new Vector(new Point(), segment.ps).add(deltaVectorNorm.multiply(edgePointsBorderDistance));
  const deltaVectors = [vectorToStart];
  for (let i = 1; i < testPoints; i++) {
    deltaVectors.push(deltaVectors[0].add(deltaVectorNorm.multiply(i * deltaLength)));
  }
  const deltaPoints = deltaVectors.map(vector => new Point(vector.x, vector.y));
  return deltaPoints.every(point => point.on(polygon));
}

/**
 *
 *
 * @param {Flatten.Polygon} polygon
 * @param {number} planksAngle - In rad
 * @param {number} [minInnerLineLength=20] - Sides shorter than this value will be ignored
 * @param {number} [angleTolerance=0.0001] - Lines are considered parallel if the angle between them is smaller than this value
 * @returns {Flatten.Segment[]} An array of segments considered inner lines (Breakerboard placement candidates)
 */
function getDivisionsFromInnerLines(polygon, planksAngle, minInnerLineLength = 20, angleTolerance = 0.0001) {
  // Recommended Inner lines (aka 'Breakerboard') are always perpendicular to Planks
  // the polygon perimeter direction CW or CCW is important to identify dents
  const isPolygonCW = [...polygon.faces][0].orientation() === ORIENTATION.CW;
  const breakerboardVector = new Vector(Math.cos(planksAngle + RAD90), Math.sin(planksAngle + RAD90));
  let edgeVectors = [];
  for (const edge of [...polygon.faces][0]) {
    edgeVectors.push(new Vector(edge.shape.ps, edge.shape.pe));
  }
  edgeVectors = [edgeVectors[edgeVectors.length - 1], ...edgeVectors, edgeVectors[0]];
  const polyVertices = [...polygon.vertices, polygon.vertices[0]];
  const innerEdges = [];

  for (let i = 1; i < edgeVectors.length - 1; i++) {
    if (
      smallestAngleBetween(edgeVectors[i], breakerboardVector) <= angleTolerance &&
      edgeVectors[i].length >= minInnerLineLength
    ) {
      // only dents produce valid inner breakerboard
      if (isInnerBorder(edgeVectors[i], edgeVectors[i - 1], edgeVectors[i + 1], isPolygonCW)) {
        innerEdges.push(new Segment(polyVertices[i - 1], polyVertices[i]));
      }
    }
  }
  return innerEdges;
}
/**
 *
 *
 * @param {Flatten.Polygon} polygon
 * @param {number} planksAngle - In rad
 * @param {number} breakerboardWidth - Width of the breakerboard area
 * @param {number} [angleTolerance=0.0001] - Lines are considered parallel if the angle between them is smaller than this value
 * @param {number} [tolerance=0.0001]
 * @returns {Flatten.Segment[]} = Segments of length equal to the border which has produced them, displaced by breakerboardWidth to the inner part of the polygon
 */
function getDivisionsFromOuterLines(
  polygon,
  planksAngle,
  breakerboardWidth,
  angleTolerance = 0.0001,
  tolerance = 0.0001
) {
  // Recommended Inner lines are always perpendicular to Planks
  // the polygon perimeter direction CW or CCW is important to identify dents
  const isPolygonCW = [...polygon.faces][0].orientation() === ORIENTATION.CW;
  let shiftFromBorder = new Vector(
    breakerboardWidth * Math.cos(planksAngle),
    breakerboardWidth * Math.sin(planksAngle)
  );
  if (shiftFromBorder.x > tolerance || (Math.abs(shiftFromBorder.x) < tolerance && shiftFromBorder.y < 0)) {
    shiftFromBorder = shiftFromBorder.invert();
  }
  if (isPolygonCW) shiftFromBorder = shiftFromBorder.invert();

  const breakerboardVector = new Vector(Math.cos(planksAngle + RAD90), Math.sin(planksAngle + RAD90));
  let edgeVectors = [];
  const polyVertices = [];
  for (const edge of [...polygon.faces][0]) {
    edgeVectors.push(new Vector(edge.shape.ps, edge.shape.pe));
    polyVertices.push(edge.shape.ps);
  }
  edgeVectors = [edgeVectors[edgeVectors.length - 1], ...edgeVectors, edgeVectors[0]];
  polyVertices.push(polyVertices[0]);
  const cornerEdges = [];
  for (let i = 1; i < edgeVectors.length - 1; i++) {
    if (smallestAngleBetween(edgeVectors[i], breakerboardVector) <= angleTolerance) {
      // only corners produce valid breakerboard
      if (isOuterBorder(edgeVectors[i], edgeVectors[i - 1], edgeVectors[i + 1], isPolygonCW)) {
        let edgePs;
        let edgePe;
        if (polyVertices[i - 1].lessThan(polyVertices[i])) {
          edgePs = new Point(polyVertices[i - 1].x + shiftFromBorder.x, polyVertices[i - 1].y + shiftFromBorder.y);
          edgePe = new Point(polyVertices[i].x + shiftFromBorder.x, polyVertices[i].y + shiftFromBorder.y);
        } else {
          edgePs = new Point(polyVertices[i - 1].x - shiftFromBorder.x, polyVertices[i - 1].y - shiftFromBorder.y);
          edgePe = new Point(polyVertices[i].x - shiftFromBorder.x, polyVertices[i].y - shiftFromBorder.y);
        }
        cornerEdges.push(new Segment(edgePs, edgePe));
      }
    }
  }
  return cornerEdges;
}
/**
 * This function orders an array of points in place
 *
 * @param {Flatten.Points} points - Points to order from smaller to bigger. They must belong to a single line
 * @param {number} [tolerance=0.0001] - Points which x difference is below this tolerance will be considered equal
 */
function orderPointsBelongingToLine(points, tolerance = 0.0001) {
  points.sort((a, b) => {
    if (Math.abs(a.x - b.x) < tolerance) return a.y - b.y;
    return a.x - b.x;
  });
}
/**
 * Shortens/enlarges a segment so it ends and starts in the outer border of a polygon.
 * The result is an array of segments, as the segment might be splitted in more than one segment
 * if the polygon has, for example, holes.
 * @param {Flatten.Segment} segment - Segment to shorten/enlarge
 * @param {Flatten.Polygon} polygon - Limits for the segment
 * @returns {Flatten.Segment[]} The resulting segments
 */
function constrainSegmentToBorders(segment, polygon, tolerance = 0.0001) {
  let ordSegment = segment;
  if (ordSegment.pe.lessThan(ordSegment.ps)) {
    ordSegment = ordSegment.reverse();
  }
  const intersectPoints = polygon.intersect(new Line(segment.ps, segment.pe));
  orderPointsBelongingToLine(intersectPoints, tolerance);
  const polySegments = [];
  for (let i = 0; i < intersectPoints.length - 1; i++) {
    const point = intersectPoints[i];
    let j;
    for (j = i + 1; j < intersectPoints.length; j++) {
      if (
        !point.equalTo(intersectPoints[j]) &&
        !checkIfContains(polygon, new Segment(point, intersectPoints[j]), 5, tolerance)
      ) {
        //if (!point.equalTo(intersectPoints[j]) && !polygon.contains(new Segment(point, intersectPoints[j]))) {
        j = j - 1;
        break;
      }
    }
    if (j === intersectPoints.length) {
      j--;
    }
    if (!point.equalTo(intersectPoints[j])) {
      if (segment.pe.lessThan(segment.ps)) {
        polySegments.push(new Segment(intersectPoints[j], point));
      } else {
        polySegments.push(new Segment(point, intersectPoints[j]));
      }
    }
    i = j;
  }
  const filtered = polySegments.filter(
    segment =>
      (segment.ps.lessThan(ordSegment.pe) || segment.contains(ordSegment.pe)) &&
      (!segment.ps.lessThan(ordSegment.ps) || segment.contains(ordSegment.ps))
  );
  return filtered;
}

/**
 *
 *
 * @param {number} angle - In rad
 * @returns {Flatten.Matrix} The resulting rotation matrix
 */
function rotationMatrix(angle) {
  return new Matrix(Math.cos(angle), Math.sin(angle), -Math.sin(angle), Math.cos(angle), 0, 0);
}
/**
 * Returns the resulting segment(s) of subtracting various segments from a larger one
 * Segments must belong to the same line
 *
 * @param {Flatten.Segment[]} segments - Segments to subtract
 * @param {Flatten.Segment} fromSegment - Largest segment
 * @returns {Flatten.Segment[]}
 */
function subtractSegments(segments, fromSegment) {
  const positiveDirection = fromSegment.ps.lessThan(fromSegment.pe) ? true : false;
  const vertices = [...fromSegment.vertices];
  segments.forEach(segment => {
    vertices.push(...segment.vertices);
  });
  orderPointsBelongingToLine(vertices);
  const subtractResult = [];
  vertices.slice(0, -1).forEach((vertex, i) => {
    if (i % 2 === 0) {
      let slice = new Segment(vertex, vertices[i + 1]);
      if (positiveDirection) slice = slice.reverse();
      if (!slice.isZeroLength()) subtractResult.push(slice);
    }
  });
  return subtractResult;
}

/**
 * Obtains secondary cut for an inner line Breakerboard cut. The secondary cut can be one or more segments
 *
 * Attention: this currently only works for vertical cuts
 * @param {{edge: Flatten.Segment, outerEdges: Flatten.Segment[], innerEdge: Flatten.Segment}} cut - Inner line breakerboard object
 * @param {boolean} isPolygonCW - Rotation direction of the polygon main face
 * @param {number} breakerboardWidth - Width of the breakerboard area
 * @param {boolean} [shortestBreakerboard=true] - Selects shortest vs longest of the two possible Breakerboard ends
 * @returns {Flatten.Segment[]} Array of segments containing the preferred cut
 */
function getSecondBreakerboardCutFromInnerLine(cut, isPolygonCW, breakerboardWidth, shortestBreakerboard = true) {
  const outerEdgesLength = cut.outerEdges.reduce((acc, outerEdge) => acc + outerEdge.length, 0);
  let cutLineXShift = outerEdgesLength > cut.innerEdge.length ? -1 * breakerboardWidth : breakerboardWidth;
  cutLineXShift = cut.edge.ps.y > cut.edge.pe.y ? -1 * cutLineXShift : cutLineXShift;
  cutLineXShift = isPolygonCW ? -1 * cutLineXShift : cutLineXShift;
  cutLineXShift = shortestBreakerboard ? cutLineXShift : -1 * cutLineXShift;
  const cutSegments = [];
  if (outerEdgesLength < cut.innerEdge.length && shortestBreakerboard) {
    cut.outerEdges.forEach(outerEdge => {
      cutSegments.push(
        new Segment(
          new Point(outerEdge.ps.x + cutLineXShift, outerEdge.ps.y),
          new Point(outerEdge.pe.x + cutLineXShift, outerEdge.pe.y)
        )
      );
    });
  } else {
    cutSegments.push(
      new Segment(
        new Point(cut.innerEdge.ps.x + cutLineXShift, cut.innerEdge.ps.y),
        new Point(cut.innerEdge.pe.x + cutLineXShift, cut.innerEdge.pe.y)
      )
    );
  }
  return cutSegments;
}

/**
 * Calculates the required vertical cuts to divide a polygon uniformly.
 * Attention: this currently only works for vertical cuts.
 * @param {Flatten.Polygon} polygon - Polygon to divide according to.
 * @param {number} maxPlankLength - Length of the available planks.
 * @param {number} breakerboardWidth - Width of the breakerboard area.
 * @returns {Flatten.Segment[]} The resulting vertical cuts which divide the area uniformly.
 */
function dividePolyHorizontalPlanks(polygon, maxPlankLength, breakerboardWidth) {
  const width = Math.abs(polygon.box.xmin - polygon.box.xmax);
  const cutSegments = [];
  if (width > maxPlankLength) {
    const totalCuts = Math.ceil(width / (maxPlankLength - 0.5 * breakerboardWidth));
    for (let i = 1; i < totalCuts; i++) {
      const middleX = polygon.box.xmin + (width / totalCuts) * i;
      const verticalCuts = [
        new Line(new Point(middleX - 0.5 * breakerboardWidth, 0), new Point(middleX - 0.5 * breakerboardWidth, 1)),
        new Line(new Point(middleX + 0.5 * breakerboardWidth, 0), new Point(middleX + 0.5 * breakerboardWidth, 1)),
      ];
      verticalCuts.forEach(line => {
        const intersectPoints = line.intersect(polygon);
        orderPointsBelongingToLine(intersectPoints);
        cutSegments.push(
          ...constrainSegmentToBorders(
            new Segment(intersectPoints[0], intersectPoints[intersectPoints.length - 1]),
            polygon
          )
        );
      });
    }
  }
  return cutSegments;
}

function dividePolyHorizontalCuts(polygon, maxPlankLength, breakerboardWidth) {
  const width = Math.abs(polygon.box.xmin - polygon.box.xmax);
  const cutSegments = [];
  if (width > maxPlankLength) {
    const totalCuts = Math.ceil(width / (maxPlankLength - 0.5 * breakerboardWidth));
    for (let i = 1; i < totalCuts; i++) {
      const middleX = polygon.box.xmin + (width / totalCuts) * i;
      const verticalCut = new Line(new Point(middleX, 0), new Point(middleX, 1));

      const intersectPoints = verticalCut.intersect(polygon);
      orderPointsBelongingToLine(intersectPoints);
      cutSegments.push(
        ...constrainSegmentToBorders(
          new Segment(intersectPoints[0], intersectPoints[intersectPoints.length - 1]),
          polygon
        )
      );
    }
  }
  return cutSegments;
}

function getMainPolygonFaceIndex(polygon) {
  const faceAreas = [...polygon.faces].map(face => face.area());
  const maxArea = Math.max(...faceAreas);
  // biggest area is considered the outer border/face of the polygon
  return faceAreas.findIndex(area => area === maxArea);
}

/**
 * Inverts the secondary faces direction of a polygon so they are considered holes.
 *
 * @param {Flatten.Polygon} polygon - Polygon to modify, with a single Island and 0 or more holes.
 */
function setSecondaryFacesAsHoles(polygon, outerFaceIndex = 0) {
  const polyMainOrientation = [...polygon.faces][outerFaceIndex].orientation();
  // secondary faces will be considered holes
  [...polygon.faces].forEach((face, i) => {
    if (i !== outerFaceIndex && face.orientation() === polyMainOrientation) {
      face.reverse();
    }
  });
}

function getPolyWithoutHoles(polygon, outerFaceIndex = 0) {
  const noHolesPoly = new Polygon();
  noHolesPoly.addFace([...polygon.faces][outerFaceIndex]);
  return noHolesPoly;
}

function createInnerLinesBreakerboard(
  polygon,
  noHolesPolygon,
  polyOrientation,
  angle,
  breakerboardWidth,
  priorityShortestBreakerboard
) {
  const areas = [];
  const breakerboard = [];
  let multiCutter = new Polygon();

  getDivisionsFromInnerLines(polygon, angle).forEach(edge => {
    //ignore holes
    let innerEdge = constrainSegmentToBorders(edge, noHolesPolygon)[0];
    const outerEdgesLength = subtractSegments([edge], innerEdge).reduce((acc, outerEdge) => acc + outerEdge.length, 0);

    if (polyOrientation !== ORIENTATION.CW) {
      innerEdge = innerEdge.reverse();
    }
    if (priorityShortestBreakerboard && innerEdge.length > outerEdgesLength) {
      innerEdge = innerEdge.reverse();
    }
    const cutter = trapezoidFromSide(innerEdge, breakerboardWidth, noHolesPolygon);
    if ([...cutter.faces][0].orientation() !== polyOrientation) [...cutter.faces][0].reverse();
    multiCutter = unify(multiCutter, cutter);
  });
  const breakerboardAreas = intersect(polygon, multiCutter);
  const reducedAreas = subtract(polygon, multiCutter);
  areas.push(...createPolygonsFromFaces(reducedAreas, polyOrientation));
  breakerboard.push(...createPolygonsFromFaces(breakerboardAreas, polyOrientation));
  return [areas, breakerboard];
}

function createOuterLinesBreakerboard(
  origPolygon,
  polygons,
  noHolesPolygon,
  polyOrientation,
  angle,
  breakerboardWidth
) {
  let unifiedPolygon = polygons[0];
  polygons.slice(1).forEach(poly => (unifiedPolygon = unify(unifiedPolygon, poly)));

  const cutsOuter = [];
  const closingBreakerboard = getDivisionsFromOuterLines(origPolygon, angle, breakerboardWidth);
  cutsOuter.push(...closingBreakerboard.map(edge => constrainSegmentToBorders(edge, origPolygon)[0]));

  let multiCutter = new Polygon();
  cutsOuter.forEach(edge => {
    if (polyOrientation === ORIENTATION.CW) {
      edge = edge.reverse();
    }
    const cutter = trapezoidFromSide(edge, breakerboardWidth, noHolesPolygon);
    if ([...cutter.faces][0].orientation() !== polyOrientation) [...cutter.faces][0].reverse();
    multiCutter = unify(multiCutter, cutter);
  });

  const breakerboardAreas = intersect(unifiedPolygon, multiCutter);
  const reducedAreas = subtract(unifiedPolygon, multiCutter);

  return [
    createPolygonsFromFaces(reducedAreas, polyOrientation),
    createPolygonsFromFaces(breakerboardAreas, polyOrientation),
  ];
}

/**
 * Calculates the segments required to cut a deck plan so no area has breakerboard bigger than the available length
 * The area is segmented uniformly, except if it contains inner edges, in which case placing a Breakerboard there is prioritized
 * This function must be executed independently for each plan island.
 * @param {Flatten.Polygon} polygon - Deck plan to segment
 * @param {number} planksAngle - In rad
 * @param {number} breakerboardWidth - Width of the breakerboard area
 * @param {number} [maxPlankLength=150] - The length of the planks to use
 * @param {boolean} [priorityShortestBreakerboard=true] - For inner line Breakerboard, the secondary cut wll prioritize obtaining shorter Breakerboard areas
 * @param {boolean} [addClosingBreakerboard=true] - Will add Breakerboard to polygon outer border perpendicular to planksAngle
 * @returns {[Flatten.Segment[], Flatten.Polygon[]]} - An array of segments which cut the deck plan as defined, and an array of the resulting cut polygons (only outer and inner Breakerboard)
 */
function autoCalcBreakerboard(
  polygon,
  planksAngle,
  breakerboardWidth,
  maxPlankLength = 150,
  priorityShortestBreakerboard = true,
  addClosingBreakerboard = true
) {
  const mainFaceIndex = getMainPolygonFaceIndex(polygon);
  const polyOrientation = [...polygon.faces][mainFaceIndex].orientation();
  setSecondaryFacesAsHoles(polygon, mainFaceIndex);
  // rotate polygon according to plank angle so we can use boundary box w dimensions
  const rotatedPoly = polygon.transform(rotationMatrix(-planksAngle));
  const noHolesPoly = getPolyWithoutHoles(rotatedPoly, mainFaceIndex);

  if (rotatedPoly.box.xmax - rotatedPoly.box.xmin <= maxPlankLength) {
    // no need for breakerboard
    return [];
  } else {
    // angle is 0 due to polygon already rotated
    let [newAreas, newBreakerboard] = createInnerLinesBreakerboard(
      rotatedPoly,
      noHolesPoly,
      polyOrientation,
      0,
      breakerboardWidth,
      priorityShortestBreakerboard
    );

    if (addClosingBreakerboard) {
      let sideBreakerboard;
      [newAreas, sideBreakerboard] = createOuterLinesBreakerboard(
        rotatedPoly,
        newAreas,
        noHolesPoly,
        polyOrientation,
        0,
        breakerboardWidth
      );
      newBreakerboard.push(...sideBreakerboard);
    }

    const nextAreas = [];
    newAreas.forEach(area => {
      const cuts = dividePolyHorizontalCuts(area, maxPlankLength, breakerboardWidth);
      let multiCutter = new Polygon();
      cuts.forEach(cut => {
        const cutter = trapezoidFromCenter(cut, breakerboardWidth, noHolesPoly);
        if ([...cutter.faces][0].orientation() !== polyOrientation) {
          [...cutter.faces][0].reverse();
        }
        multiCutter = unify(multiCutter, cutter);
      });
      if (cuts.length !== 0) {
        const breakerboardAreas = intersect(area, multiCutter);
        const reducedAreas = subtract(area, multiCutter);
        nextAreas.push(...createPolygonsFromFaces(reducedAreas, [...rotatedPoly.faces][0].orientation()));
        newBreakerboard.push(...createPolygonsFromFaces(breakerboardAreas, [...rotatedPoly.faces][0].orientation()));
      } else {
        nextAreas.push(area);
      }
    });
    newAreas = nextAreas;

    //transform back to original orientation
    return [
      newAreas.map(poly => poly.transform(rotationMatrix(planksAngle))),
      newBreakerboard.map(poly => poly.transform(rotationMatrix(planksAngle))),
    ];
  }
}

export {
  checkIfContains,
  isInnerBorder,
  isInnerEdge,
  isOuterBorder,
  removeDuplicatedLines,
  orderPointsBelongingToLine,
  getDivisionsFromInnerLines,
  getDivisionsFromOuterLines,
  constrainSegmentToBorders,
  autoCalcBreakerboard,
  getMainPolygonFaceIndex,
  dividePolyHorizontalPlanks,
  getPolyWithoutHoles,
  getSecondBreakerboardCutFromInnerLine,
  multiSplitPolygons,
  rotationMatrix,
  setSecondaryFacesAsHoles,
  subtractSegments,
  smallestAngleBetween,
};
